import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronRight,
  CheckCircle2,
  Loader2,
  Mail,
  ShieldCheck,
  Printer,
  Package2,
  TrendingUp,
  Phone,
  Building2,
  Send,
  ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * Landing publique B2B — "Devenez revendeur Maison Thaï"
 *
 * Route : /devenir-revendeur (publique, pas de auth)
 * Objectif : convertir les visiteurs en candidatures revendeur.
 *
 * Structure :
 *  1. Hero (proposition de valeur + CTA)
 *  2. Pourquoi nous (4 cards)
 *  3. Comment ça marche (3 étapes)
 *  4. FAQ courte
 *  5. Formulaire de candidature
 *  6. Footer (contact direct)
 */
export default function BecomeReseller() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
    postal_code: "",
    business_type: "shop",
    expected_volume: "10-50",
    message: "",
  });

  const handleChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error("Merci de remplir les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      await api.post("/shop/reseller-applications", form);
      setSubmitted(true);
      toast.success("Candidature envoyée. À très vite !");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Erreur d'envoi. Réessayez ou contactez-nous directement.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="reseller-success-page">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F2EC] flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#1F4E3D]" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1C1A] mb-4">
            Merci, votre candidature est en route 🙏
          </h1>
          <p className="text-lg text-[#575A57] leading-relaxed mb-8">
            Notre équipe revient vers vous dans les <strong>48h ouvrées</strong> avec une proposition personnalisée.
            En attendant, n'hésitez pas à découvrir notre boutique.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/boutique"
              className="inline-flex items-center gap-2 px-6 py-3 rounded bg-[#1F4E3D] hover:bg-[#173B2E] text-white text-sm uppercase tracking-wider transition-colors"
              data-testid="success-shop-link"
            >
              Découvrir la boutique <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:info@maison-thai.ch"
              className="inline-flex items-center gap-2 px-6 py-3 rounded border border-[#1F4E3D] text-[#1F4E3D] text-sm uppercase tracking-wider hover:bg-[#1F4E3D]/5 transition-colors"
            >
              <Mail className="w-4 h-4" /> Nous écrire
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="become-reseller-page">
      <Header />

      {/* HERO */}
      <section className="bg-[#1F4E3D] text-[#FAFAF7]">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A8C9B6] mb-4">
            Programme revendeur B2B · Maison Thaï
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium leading-tight max-w-3xl">
            Vendez la <span className="italic">vraie cuisine thaïe artisanale</span> dans votre commerce.
          </h1>
          <p className="text-lg md:text-xl text-[#A8C9B6] mt-6 max-w-2xl leading-relaxed">
            Maison Thaï équipe shops, magasins alimentaires et kiosques en Suisse romande avec des barquettes prêtes-à-vendre,
            DLC longue, étiquetage automatique, et marges revendeur confortables.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/boutique/inscription?type=pro"
              className="inline-flex items-center gap-2 px-8 py-4 rounded bg-[#C1A063] hover:bg-[#A78A4C] text-[#0A0A0A] font-semibold text-sm uppercase tracking-wider transition-colors"
              data-testid="hero-create-pro-account-btn"
            >
              Créer mon compte pro <ChevronRight className="w-4 h-4" />
            </Link>
            <a
              href="#candidature"
              className="inline-flex items-center gap-2 px-6 py-4 rounded border border-[#C1A063] text-[#C1A063] hover:bg-[#C1A063]/10 text-sm uppercase tracking-wider transition-colors"
              data-testid="hero-apply-btn"
            >
              ou postuler par formulaire
            </a>
            <span className="text-sm text-[#A8C9B6] w-full mt-2">
              · Aucun engagement · Réponse sous 48h
            </span>
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#1F4E3D] mb-3">
          Pourquoi nous
        </div>
        <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-12 max-w-2xl">
          Un partenaire qui fait le travail compliqué pour vous.
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Feature
            icon={<TrendingUp className="w-6 h-6 text-[#1F4E3D]" />}
            title="Marges revendeur attractives"
            text="Jusqu'à -20% selon volume. Plus vous commandez, plus votre marge augmente. Paliers automatiques, transparents."
          />
          <Feature
            icon={<ShieldCheck className="w-6 h-6 text-[#1F4E3D]" />}
            title="Conformité alimentaire LARGS"
            text="Étiquettes nutritionnelles, allergènes, traçabilité par lot, DLC : tout est conforme à la loi suisse, prêt à l'emploi."
          />
          <Feature
            icon={<Printer className="w-6 h-6 text-[#1F4E3D]" />}
            title="Imprimables clé en main"
            text="Étiquettes de rayon, présentoirs, fiches techniques en FR / DE / IT — vous imprimez, vous vendez. Pas de design à faire."
          />
          <Feature
            icon={<Package2 className="w-6 h-6 text-[#1F4E3D]" />}
            title="Packs prêts-à-vendre"
            text="Starter Pack 24 barquettes mixtes, packs spéciaux saisonniers. Un clic, une commande, un présentoir prêt."
          />
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="bg-[#F4F4F0] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#1F4E3D] mb-3">
            Comment ça marche
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-12 max-w-2xl">
            De votre candidature à votre premier rayon, en 7 jours.
          </h2>
          <ol className="grid md:grid-cols-3 gap-6">
            <Step
              num="01"
              title="Candidatez"
              text="Remplissez le formulaire en bas de page (2 minutes). Pas de paperasse."
            />
            <Step
              num="02"
              title="Validation 48h"
              text="Nous vous contactons sous 48h pour discuter de vos besoins et vous proposer un palier de remise."
            />
            <Step
              num="03"
              title="Premier rayon"
              text="Première livraison + kit marketing imprimable + accès à votre espace pro. Vous vendez."
            />
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#1F4E3D] mb-3">FAQ</div>
        <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-10">
          Vos questions, nos réponses.
        </h2>
        <div className="space-y-4">
          <FaqItem
            q="Quels sont les volumes minimum ?"
            a="Aucun minimum strict. Nous démarrons même avec 10 barquettes/mois pour les petites surfaces. Plus vous montez en volume, plus votre remise augmente automatiquement."
          />
          <FaqItem
            q="Combien de temps se conservent les barquettes ?"
            a="Notre conditionnement permet une DLC de 8 à 10 jours en chaîne du froid (≤ 5°C). Idéal pour un rayon traiteur ou un kiosque alimentaire."
          />
          <FaqItem
            q="Et les étiquettes en allemand ou italien ?"
            a="Toutes nos étiquettes existent en FR / DE / IT. Nous adaptons le rayon automatiquement selon votre canton. Conforme LARGS."
          />
          <FaqItem
            q="Y a-t-il des frais de mise en place ?"
            a="Non. Pas de droits d'entrée, pas de royalties. Vous payez uniquement ce que vous commandez."
          />
          <FaqItem
            q="Je suis un restaurant, pas un magasin. Puis-je quand même devenir partenaire ?"
            a="Oui. Nous travaillons aussi avec des restaurants qui souhaitent compléter leur carte avec nos spécialités. Mentionnez-le dans votre message."
          />
        </div>
      </section>

      {/* FORMULAIRE */}
      <section id="candidature" className="bg-[#1F4E3D] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A8C9B6] mb-3">
            Candidature
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#FAFAF7] mb-3">
            Présentez-nous votre commerce.
          </h2>
          <p className="text-[#A8C9B6] mb-10">
            2 minutes pour remplir, réponse sous 48h ouvrées.
          </p>

          <form
            onSubmit={submit}
            className="bg-[#FAFAF7] rounded-lg p-6 md:p-8 space-y-5 shadow-xl"
            data-testid="reseller-application-form"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Nom du commerce *"
                placeholder="Boucherie du Pont SA"
                value={form.business_name}
                onChange={handleChange("business_name")}
                testid="field-business-name"
                required
              />
              <Field
                label="Votre nom *"
                placeholder="Jean Dupont"
                value={form.contact_name}
                onChange={handleChange("contact_name")}
                testid="field-contact-name"
                required
              />
              <Field
                label="Email *"
                type="email"
                placeholder="contact@votrecommerce.ch"
                value={form.email}
                onChange={handleChange("email")}
                testid="field-email"
                required
              />
              <Field
                label="Téléphone"
                type="tel"
                placeholder="+41 79 123 45 67"
                value={form.phone}
                onChange={handleChange("phone")}
                testid="field-phone"
              />
              <Field
                label="NPA"
                placeholder="1950"
                value={form.postal_code}
                onChange={handleChange("postal_code")}
                testid="field-postal-code"
              />
              <Field
                label="Ville"
                placeholder="Sion"
                value={form.city}
                onChange={handleChange("city")}
                testid="field-city"
              />
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                  Type de commerce
                </label>
                <select
                  value={form.business_type}
                  onChange={handleChange("business_type")}
                  className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
                  data-testid="field-business-type"
                >
                  <option value="shop">Shop / Convenience</option>
                  <option value="magasin_alimentaire">Magasin alimentaire</option>
                  <option value="kiosque">Kiosque</option>
                  <option value="restaurant">Restaurant / Take-away</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                  Volume mensuel envisagé
                </label>
                <select
                  value={form.expected_volume}
                  onChange={handleChange("expected_volume")}
                  className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
                  data-testid="field-expected-volume"
                >
                  <option value="10-50">10 à 50 barquettes / mois</option>
                  <option value="50-200">50 à 200 barquettes / mois</option>
                  <option value="200-500">200 à 500 barquettes / mois</option>
                  <option value="500+">Plus de 500 / mois</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                Votre message (facultatif)
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={handleChange("message")}
                placeholder="Décrivez votre commerce, votre clientèle, vos attentes…"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D] resize-y"
                data-testid="field-message"
              />
            </div>

            <p className="text-xs text-[#575A57]">
              En soumettant ce formulaire, vous acceptez d'être recontacté(e) par notre équipe.
              Vos données restent strictement confidentielles.
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded bg-[#C1A063] hover:bg-[#A78A4C] disabled:opacity-60 text-[#0A0A0A] font-semibold text-sm uppercase tracking-wider transition-colors"
              data-testid="submit-application-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer ma candidature
            </button>
          </form>
        </div>
      </section>

      {/* CONTACT DIRECT */}
      <section className="bg-[#F4F4F0] py-12 text-center">
        <p className="text-[#1A1C1A] mb-3">Vous préférez nous parler directement ?</p>
        <div className="flex items-center justify-center gap-6 flex-wrap text-sm">
          <a href="mailto:info@maison-thai.ch" className="inline-flex items-center gap-2 text-[#1F4E3D] hover:underline">
            <Mail className="w-4 h-4" /> info@maison-thai.ch
          </a>
          <a href="tel:+41279999999" className="inline-flex items-center gap-2 text-[#1F4E3D] hover:underline">
            <Phone className="w-4 h-4" /> Nous appeler
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#1F4E3D]" />
          <span className="font-heading text-lg text-[#1A1C1A]">Maison Thaï</span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/boutique" className="text-[#575A57] hover:text-[#1F4E3D]">
            Boutique
          </Link>
          <a href="#candidature" className="text-[#C1A063] font-semibold hover:underline">
            Devenir revendeur
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-[#A8C9B6] py-8 text-center text-xs">
      © Maison Thaï · Sion · Cuisine artisanale · Tous droits réservés
    </footer>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/30 transition-colors">
      <div className="w-10 h-10 rounded-md bg-[#E8F2EC] flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-heading text-lg text-[#1A1C1A] mb-2">{title}</h3>
      <p className="text-sm text-[#575A57] leading-relaxed">{text}</p>
    </div>
  );
}

function Step({ num, title, text }) {
  return (
    <li className="bg-white border border-[#E5E5E0] rounded-lg p-6">
      <div className="font-mono text-3xl text-[#C1A063] mb-3">{num}</div>
      <h3 className="font-heading text-lg text-[#1A1C1A] mb-2">{title}</h3>
      <p className="text-sm text-[#575A57] leading-relaxed">{text}</p>
    </li>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="bg-[#FAFAF7] border border-[#E5E5E0] rounded-lg p-4 group" data-testid="faq-item">
      <summary className="cursor-pointer text-[#1A1C1A] font-medium flex items-center justify-between">
        <span>{q}</span>
        <ChevronRight className="w-4 h-4 text-[#C1A063] group-open:rotate-90 transition-transform" />
      </summary>
      <p className="text-sm text-[#575A57] mt-3 leading-relaxed">{a}</p>
    </details>
  );
}

function Field({ label, type = "text", placeholder, value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
        data-testid={testid}
      />
    </div>
  );
}
